import { Box, Snackbar, TextField } from '@mui/material';
import { AccountBalance } from '@mui/icons-material';
import { useIsMutating } from '@tanstack/react-query';
import _ from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import AccordionSection from '../components/AccordionSection';
import ConfirmationModal from '../components/ConfirmationModal';
import ActivityLogsSection from '../components/activityLogs/ActivityLogsSection';
import AttachmentsSection from '../components/attachments/AttachmentsSection';
import AccountSearchAutocomplete from '../components/common/AccountSearchAutocomplete/AccountSearchAutocomplete';
import ItemHeader from '../components/common/ItemHeader/ItemHeader';
import MoreOptions from '../components/common/MoreOptions/MoreOptions';
import TagsSection from '../components/common/TagsSection/TagsSection';
import TextFieldWithLink from '../components/common/TextFieldWithLink/TextFieldWithLink';
import EventsTimeline from '../components/common/eventTimeline/EventsTimeline';
import NotesSection from '../components/notes/NotesSection';
import { DisableContext } from '../contexts/DisableContextProvider';
import { ROUTE_PATHS } from '../routes/Routes';
import { useGetAccountById } from '../services/react-query/accounts';
import { useCreateContact, useGetContactById, useUpdateContact } from '../services/react-query/contacts';
import { useArchiveContact } from '../services/react-query/contacts/useArchiveContact';
import { useDestroyContact } from '../services/react-query/contacts/useDestroyContact';
import { mutationKeys } from '../services/react-query/mutationKeys';
import { useGetAllPreferences } from '../services/react-query/preferences';
import {
  inputContainerStyling,
  inputDividerStyling,
  itemActivitySectionStyling,
  itemContainerStyling,
  itemInnerLayoutStyling,
  itemWrapperStyling,
} from '../styles/sxStyling';
import { ActivityLogOwnerType } from '../types/ActivityLog';
import { AttachmentOwnerType } from '../types/Attachment';
import { Contact } from '../types/Contact';
import { NoteableType } from '../types/Note';
import { PREFERENCES_VALID_KEYS, Preference } from '../types/Preference';
import { UserConfigurableModule, UserSettingsCollapseKeys } from '../types/User';
import { Loading } from '../uikit';
import { errorPopulator, uuidFinder } from '../utility/inputUtils';
import PreferencePicker from '../components/preferences/picker/PreferencePicker';

const ContactItem: React.FC = () => {
  const navigate = useNavigate();
  const { uuid } = useParams();
  const [searchParams] = useSearchParams();

  const [contact, setContact] = useState<Contact | null>(null);
  const [saved, setSaved] = useState<boolean>(false);
  const [deletionConfirmationOpen, setDeletionConfirmationOpen] = useState<boolean>(false);

  const { data: contactData, isLoading: isGetContactByIdLoading } = useGetContactById({ uuid: _.toString(uuid) });

  const accountUuidFromParams = searchParams.get('account') || undefined;
  const accountUuidFromContact = contact?.account_uuid;

  const { data: accountDataFromContact } = useGetAccountById({
    uuid: _.toString(accountUuidFromParams || accountUuidFromContact),
    enabled: !!accountUuidFromParams || !!accountUuidFromContact,
  });

  const saveCallback = () => setSaved(true);
  const { mutate: createContact } = useCreateContact({
    onSuccessCallback: (created: Contact) => {
      saveCallback();
      navigate(`/${ROUTE_PATHS.CONTACTS}/${created.uuid}`);
    },
  });
  const { mutate: updateContact } = useUpdateContact({ uuid: _.toString(uuid), onSuccessCallback: saveCallback });
  const isCreateContactMutation = useIsMutating({ mutationKey: [mutationKeys.createContact] });
  const isUpdateContactMutation = useIsMutating({ mutationKey: [mutationKeys.updateContact] });

  const returnToList = () => navigate(`/${ROUTE_PATHS.CONTACTS}`);
  const { mutate: destroyContact } = useDestroyContact({ uuid: _.toString(uuid), onSuccessCallback: returnToList });
  const { mutate: archiveContact } = useArchiveContact({ uuid: _.toString(uuid), onSuccessCallback: returnToList });

  // Parent fetches preferences and passes them to PreferencePicker.
  const { data: preferences, isFetching: isGetAllPreferencesFetching } = useGetAllPreferences({
    params: { filters: { keys: ['contact_role', 'contact_title'] } },
  });

  useEffect(() => {
    if (contactData) {
      setContact({
        ...contactData,
        account_uuid: contactData.account_uuid || accountUuidFromParams || '',
      });
    }
  }, [contactData, accountUuidFromParams]);

  const { disabled, selfDisable } = useContext(DisableContext);
  selfDisable(!!contact?.discarded_at);

  const isNew = uuid === 'new';

  if (!contact || isGetContactByIdLoading) return <Loading />;

  const updateContactData = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newContact = { ...contact, [event.target.id]: event.target.value };
    setContact(newContact as Contact);
  };

  const handleSave = () => {
    if (!contact) return;

    if (isNew) createContact(contact);
    else updateContact(contact);
  };

  const fullName = [contactData?.first_name, contactData?.last_name].filter(Boolean).join(' ');
  const email = contactData?.email ? ` (${contactData.email})` : '';
  const combinedHeader = `${fullName} ${email}`.replace(/\s+/g, ' ').trim();

  // Derive options for title/role from fetched preferences.
  const titleOptions = (preferences ?? []).filter((p) => p.key === PREFERENCES_VALID_KEYS.CONTACT_TITLE);
  const roleOptions = (preferences ?? []).filter((p) => p.key === PREFERENCES_VALID_KEYS.CONTACT_ROLE);

  return (
    <Box sx={itemWrapperStyling}>
      <ItemHeader
        title={isNew ? 'Create Contact' : combinedHeader}
        onSave={handleSave}
        disableActions={Boolean(isCreateContactMutation || isUpdateContactMutation)}
        archived={!!contact.discarded_at}
        showMoreOptions={!isNew ? <MoreOptions onDelete={destroyContact} onArchive={archiveContact} name={contact?.full_name} itemName="contact" /> : null}
        module={UserConfigurableModule.CONTACTS}
        infoItems={[
          { content: contactData?.contact_title_value ?? '—' },
          {
            icon: <AccountBalance fontSize="small" color="action" />,
            content: contactData?.account_name ?? '—',
          },
        ]}
      />

      {/* Left Side */}
      <Box sx={itemInnerLayoutStyling}>
        <Box sx={itemContainerStyling}>
          <AccordionSection title="Contact Information" collapseId={UserSettingsCollapseKeys.CONTACT_ITEM_INFORMATION}>
            <Box sx={inputContainerStyling}>
              <Box sx={inputDividerStyling}>
                <AccountSearchAutocomplete
                  value={accountDataFromContact || null}
                  error={errorPopulator(contact, 'account')}
                  onChange={(account) => setContact({ ...contact, account_uuid: account?.uuid || '' })}
                />

                <TextField
                  id="first_name"
                  label="First Name"
                  autoFocus
                  onChange={updateContactData}
                  value={contact?.first_name ?? ''}
                  disabled={disabled}
                  {...errorPopulator(contact, 'first_name')}
                />

                <TextField
                  id="last_name"
                  label="Last Name"
                  onChange={updateContactData}
                  value={contact?.last_name ?? ''}
                  disabled={disabled}
                  {...errorPopulator(contact, 'last_name')}
                />

                {/* Title (single) */}
                <PreferencePicker
                  label="Title"
                  options={titleOptions}
                  loading={isGetAllPreferencesFetching}
                  value={uuidFinder(preferences ?? [], contact?.contact_title_uuid) as Preference | null}
                  onValueChange={(pref) =>
                    setContact((prev) => (prev ? ({ ...prev, contact_title_uuid: (pref as Preference | null)?.uuid } as Contact) : prev))
                  }
                  disabled={disabled}
                  error={!!errorPopulator(contact, 'title')?.error}
                  helperText={errorPopulator(contact, 'title')?.helperText}
                />
              </Box>

              <Box sx={inputDividerStyling}>
                {/* Roles (multi) */}
                <PreferencePicker
                  label="Role"
                  options={roleOptions}
                  loading={isGetAllPreferencesFetching}
                  multiple
                  value={_.filter(preferences ?? [], (p) => _.includes(contact.roles_uuids, p.uuid))}
                  onValueChange={(vals) =>
                    setContact((prev) => (prev ? ({ ...prev, roles_uuids: (vals as Preference[]).map((p) => p.uuid) } as Contact) : prev))
                  }
                  disabled={disabled}
                  error={!!errorPopulator(contact, 'role')?.error}
                  helperText={errorPopulator(contact, 'role')?.helperText}
                  fullWidth
                />

                <TextFieldWithLink
                  linkType="email"
                  id="email"
                  label="Email"
                  onChange={updateContactData}
                  value={contact?.email ?? ''}
                  disabled={disabled}
                  {...errorPopulator(contact, 'email')}
                />

                <Box display="flex" gap={3}>
                  <TextFieldWithLink
                    linkType="phone"
                    id="phone_1"
                    label="Phone"
                    onChange={updateContactData}
                    value={contact?.phone_1 ?? ''}
                    disabled={disabled}
                    {...errorPopulator(contact, 'phone_1')}
                    fullWidth
                    sx={{ flex: 2 }}
                  />
                  <TextField
                    id="extension"
                    label="Extension"
                    onChange={updateContactData}
                    value={contact?.extension ?? ''}
                    disabled={disabled}
                    {...errorPopulator(contact, 'extension')}
                    fullWidth
                    sx={{ flex: 1 }}
                  />
                </Box>

                <TextField
                  id="phone_2"
                  label="Mobile"
                  onChange={updateContactData}
                  value={contact?.phone_2 ?? ''}
                  disabled={disabled}
                  {...errorPopulator(contact, 'phone_2')}
                />
              </Box>
            </Box>
          </AccordionSection>

          {!isNew && <EventsTimeline entityUuid={contact.uuid} entityType={'contacts'} />}

          <TagsSection tags={contact.tags} collapseId={UserSettingsCollapseKeys.CONTACT_ITEM_TAGS} setTags={(tags) => setContact({ ...contact, tags })} />

          {!isNew && (
            <AttachmentsSection collapseId={UserSettingsCollapseKeys.CONTACT_ITEM_ATTACHMENTS} ownerType={AttachmentOwnerType.CONTACT} ownerUuid={uuid ?? ''} />
          )}

          <Snackbar open={saved} onClose={() => setSaved(false)} message="Saved!" />
          <ConfirmationModal
            isOpen={deletionConfirmationOpen}
            onCancel={() => setDeletionConfirmationOpen(false)}
            onConfirm={destroyContact}
            text={`Are you sure you want to delete the contact ${contact.full_name}? This operation is irreversible!`}
          />
        </Box>

        {/* Right Side */}
        {!isNew && (
          <Box sx={itemActivitySectionStyling}>
            <ActivityLogsSection ownerUuid={uuid} ownerType={ActivityLogOwnerType.CONTACT} collapseId={UserSettingsCollapseKeys.CONTACT_ITEM_ACTIVITY_LOG} />
            <NotesSection
              noteable={{
                noteableUuid: uuid ?? '',
                noteableType: NoteableType.CONTACT,
              }}
              collapseId={UserSettingsCollapseKeys.CONTACT_ITEM_NOTES}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ContactItem;
